import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Component } from 'react';
/**
 * Error boundary component that catches JavaScript errors in child components
 * and displays a fallback UI instead of crashing the whole app.
 */
export class ErrorBoundary extends Component {
    timestamp = '';
    reloadTimer = null;
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            showDetails: false,
            isModuleError: false,
            reloadCountdown: 0,
        };
    }
    /**
     * Check if an error is a module loading failure.
     * These occur when the app is redeployed and old module URLs no longer exist.
     */
    isModuleLoadingError(error) {
        const message = error.message || '';
        return (message.includes('Failed to fetch dynamically imported module') ||
            message.includes('Loading chunk') ||
            message.includes('Failed to load module script') ||
            message.includes('error loading dynamically imported module'));
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    componentDidCatch(error, errorInfo) {
        this.timestamp = new Date().toISOString();
        const isModuleError = this.isModuleLoadingError(error);
        this.setState({ errorInfo, isModuleError });
        this.props.onError?.(error, errorInfo);
        console.error('ErrorBoundary caught error:', error, errorInfo);
        // For module loading errors, auto-reload after a short countdown
        // This handles the common case of app redeployment while user has page open
        if (isModuleError) {
            this.setState({ reloadCountdown: 3 });
            this.reloadTimer = setInterval(() => {
                this.setState((prev) => {
                    const newCountdown = prev.reloadCountdown - 1;
                    if (newCountdown <= 0) {
                        if (this.reloadTimer) {
                            clearInterval(this.reloadTimer);
                            this.reloadTimer = null;
                        }
                        window.location.reload();
                    }
                    return { reloadCountdown: newCountdown };
                });
            }, 1000);
        }
    }
    componentWillUnmount() {
        if (this.reloadTimer) {
            clearInterval(this.reloadTimer);
            this.reloadTimer = null;
        }
    }
    handleReset = () => {
        if (this.reloadTimer) {
            clearInterval(this.reloadTimer);
            this.reloadTimer = null;
        }
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            showDetails: false,
            isModuleError: false,
            reloadCountdown: 0,
        });
        this.props.onReset?.();
    };
    cancelAutoReload = () => {
        if (this.reloadTimer) {
            clearInterval(this.reloadTimer);
            this.reloadTimer = null;
        }
        this.setState({ reloadCountdown: 0 });
    };
    handleCopyDetails = () => {
        const { error, errorInfo } = this.state;
        const details = [
            `Error: ${error?.message || 'Unknown error'}`,
            `Timestamp: ${this.timestamp}`,
            `User Agent: ${navigator.userAgent}`,
            '',
            'Stack Trace:',
            error?.stack || 'No stack trace available',
            '',
            'Component Stack:',
            errorInfo?.componentStack || 'No component stack available',
        ].join('\n');
        navigator.clipboard.writeText(details).catch(() => {
            // Clipboard API might not be available
        });
    };
    toggleDetails = () => {
        this.setState((prev) => ({ showDetails: !prev.showDetails }));
    };
    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }
            return (_jsxs("div", { className: "min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-br from-gray-900 to-gray-800", children: [_jsx("div", { className: "w-20 h-20 mb-6 rounded-full bg-red-500/20 flex items-center justify-center", children: this.state.isModuleError ? (_jsx("svg", { className: "w-10 h-10 text-amber-500", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" }) })) : (_jsx("svg", { className: "w-10 h-10 text-red-500", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" }) })) }), this.state.isModuleError ? (_jsxs(_Fragment, { children: [_jsx("h1", { className: "text-xl font-bold text-white mb-2", children: "App Updated" }), _jsx("p", { className: "text-gray-400 text-center mb-4 max-w-xs", children: "A new version is available. Reloading to get the latest..." }), this.state.reloadCountdown > 0 && (_jsxs("div", { className: "mb-4 flex items-center gap-3", children: [_jsx("div", { className: "w-8 h-8 rounded-full border-2 border-violet-500 flex items-center justify-center text-violet-400 font-bold", children: this.state.reloadCountdown }), _jsx("button", { onClick: this.cancelAutoReload, className: "text-sm text-gray-500 hover:text-gray-300", children: "Cancel" })] }))] })) : (_jsx("h1", { className: "text-xl font-bold text-white mb-4", children: "Something went wrong" })), this.state.error && !this.state.isModuleError && (_jsx("p", { className: "text-red-400 text-sm mb-4 text-center max-w-md", children: this.state.error.message })), this.state.error && !this.state.isModuleError && (_jsxs("div", { className: "mb-6 w-full max-w-4xl px-4", children: [_jsxs("button", { onClick: this.toggleDetails, className: "flex items-center gap-2 text-gray-500 text-sm hover:text-gray-400 transition-colors", children: [_jsx("svg", { className: `w-4 h-4 transition-transform ${this.state.showDetails ? 'rotate-90' : ''}`, fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M9 5l7 7-7 7" }) }), this.state.showDetails ? 'Hide Details' : 'Show Details'] }), this.state.showDetails && (_jsxs("div", { className: "mt-3 p-4 bg-gray-800 rounded-lg select-text", children: [_jsxs("div", { className: "flex justify-between items-start mb-3", children: [_jsx("span", { className: "text-xs text-gray-500", children: "Error Details" }), _jsxs("button", { onClick: this.handleCopyDetails, className: "text-xs text-gray-400 hover:text-white flex items-center gap-1 transition-colors select-none", children: [_jsx("svg", { className: "w-3 h-3", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" }) }), "Copy"] })] }), _jsxs("div", { className: "space-y-3 text-xs", children: [_jsxs("div", { children: [_jsx("span", { className: "text-gray-500 block mb-1", children: "Error Message" }), _jsx("p", { className: "text-red-300 font-medium", children: this.state.error.message || 'Unknown error' })] }), _jsxs("div", { children: [_jsx("span", { className: "text-gray-500 block mb-1", children: "Timestamp" }), _jsx("span", { className: "text-gray-300 font-mono", children: this.timestamp })] }), _jsxs("div", { children: [_jsx("span", { className: "text-gray-500 block mb-1", children: "Stack Trace" }), _jsx("pre", { className: "text-red-400 overflow-auto max-h-64 whitespace-pre font-mono text-[11px] leading-relaxed p-2 bg-gray-900/50 rounded", children: this.state.error.stack })] }), this.state.errorInfo?.componentStack && (_jsxs("div", { children: [_jsx("span", { className: "text-gray-500 block mb-1", children: "Component Stack" }), _jsx("pre", { className: "text-orange-400 overflow-auto max-h-48 whitespace-pre font-mono text-[11px] leading-relaxed p-2 bg-gray-900/50 rounded", children: this.state.errorInfo.componentStack })] })), _jsxs("div", { children: [_jsx("span", { className: "text-gray-500 block mb-1", children: "Browser" }), _jsx("span", { className: "text-gray-300 font-mono break-all text-[11px]", children: navigator.userAgent })] })] })] }))] })), _jsxs("div", { className: "flex gap-3", children: [_jsx("button", { onClick: this.handleReset, className: "px-6 py-3 bg-gray-700 hover:bg-gray-600 active:scale-95 rounded-xl text-white font-medium transition-all", children: "Try Again" }), _jsx("button", { onClick: () => window.location.reload(), className: "px-6 py-3 bg-violet-600 hover:bg-violet-500 active:scale-95 rounded-xl text-white font-medium transition-all", children: "Reload Page" })] })] }));
        }
        return this.props.children;
    }
}
//# sourceMappingURL=ErrorBoundary.js.map